import { ListSkeletonLoader } from "@repo/ui";
import { Box, Button, Illustration, Typography } from "@saviynt/design-system";
import { useQueryClient } from "@tanstack/react-query";
import cx from "classnames";
import { FormattedMessage } from "react-intl";
import { useTheme } from "@repo/mfe-core";
import type { IntegrationWithRawData } from "../hooks/useIntegrations";
import { IntegrationsEmptyState } from "./IntegrationsEmptyState";
import { IntegrationsList } from "./IntegrationsList";

interface IntegrationsListContentProps {
  integrations: IntegrationWithRawData[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  isSidePanelOpen: boolean;
  selectedIntegrationId: string | null;
  isTabletMediumWidth: boolean;
  isMobileXlargeWidth: boolean;
  onRowClick: (integrationId: string) => void;
  connectionSource?: string | null;
  connectionStatus?: string | null;
  searchValue?: string;
  onAddIntegration?: () => void;
  onRetry?: () => void;
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
}

export function IntegrationsListContent({
  integrations,
  isLoading,
  isError,
  error,
  isSidePanelOpen,
  selectedIntegrationId,
  isTabletMediumWidth,
  isMobileXlargeWidth,
  onRowClick,
  connectionSource,
  connectionStatus,
  searchValue,
  onAddIntegration,
  onRetry,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
}: IntegrationsListContentProps) {
  const { theme } = useTheme();
  const queryClient = useQueryClient();

  const handleRetry = () => {
    // Refetch the integrations query instead of reloading the page
    queryClient.invalidateQueries({ queryKey: ["integrations"] });
    // Call the onRetry callback to focus the search field
    if (onRetry) {
      onRetry();
    }
  };
  const wrapperClasses = cx(
    "Integrations-sidePanelListRowGroupWrapper",
    isSidePanelOpen &&
      !isTabletMediumWidth &&
      !isMobileXlargeWidth &&
      "Integrations-sidePanelListRowGroupWrapper--isSidePanelOpen",
  );

  // Determine empty state variant based on active filters or search
  // Show "no-results" if filters are active OR search has 3+ characters
  const hasActiveFilters = Boolean(connectionSource || connectionStatus);
  const hasActiveSearch = Boolean(
    searchValue && searchValue.trim().length >= 3,
  );
  const emptyStateVariant =
    hasActiveFilters || hasActiveSearch ? "no-results" : "empty";

  return (
    <Box className="Integrations-flexBoxWithSidePanel flex-1 min-h-0 pl-10">
      {/* Loading State */}
      {isLoading && (
        <Box className={wrapperClasses}>
          <ListSkeletonLoader count={10} />
        </Box>
      )}

      {/* Error State */}
      {!isLoading && isError && (
        <Box className="flex flex-col items-center justify-center flex-1 min-h-0 p-8">
          <Typography
            kind="h3"
            className="mb-4"
            dataTheme={theme === "dark" ? "dark" : "light"}
          >
            <FormattedMessage
              id="8T9tiX"
              defaultMessage="Failed to load integrations"
              description="Error message when integrations fail to load"
            />
          </Typography>
          {error && (
            <div>
              <Illustration kind="Error" />
              <Typography
                kind="body2"
                dataTheme={theme === "dark" ? "dark" : "light"}
              >
                {error instanceof Error ? error.message : String(error)}
              </Typography>
            </div>
          )}
          <Button kind="filled" onClick={handleRetry} className="mt-4">
            <FormattedMessage
              id="AHzvcj"
              defaultMessage="Retry"
              description="Retry button"
            />
          </Button>
        </Box>
      )}

      {/* List Content Wrapper */}
      {!isLoading && !isError && (
        <>
          {integrations.length === 0 ? (
            <IntegrationsEmptyState
              variant={emptyStateVariant}
              onGetStarted={onAddIntegration}
            />
          ) : (
            <Box className={wrapperClasses}>
              <IntegrationsList
                integrations={integrations}
                selectedIntegrationId={selectedIntegrationId}
                onRowClick={onRowClick}
                fetchNextPage={fetchNextPage}
                hasNextPage={hasNextPage}
                isFetchingNextPage={isFetchingNextPage}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
